import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import type { ContractDto } from '@b-stock/contract-api-client'
import type { BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import {
  CellInputNumber,
  FormattedMessage,
  usePrecisionNumberFormat,
} from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import type { OfferingItemRow } from '../types'
import { ContractBidFulfillmentUnit } from '../values/ContractBidFulfillmentUnit'

const ns = 'Offerings.Details.form'
const rowsId = 'itemRows'

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const For = styled.span`
  ${Typography.Body3}
  color: ${designColors.neutral.black};
`

// input margin-bottom handles line wrapping near min 320px viewport width
const InputWrap = styled.span`
  display: inline-block;
  margin-bottom: 0.5rem;
  white-space: nowrap;
`

export const YourOfferCell = ({
  bidPrecision = 1,
  contract,
  row,
  rowIndex,
  currency = 'USD',
}: {
  bidPrecision?: BidPrecision
  contract?: ContractDto | null
  row: OfferingItemRow
  rowIndex: number
  currency?: string
}) => {
  const intl = useIntl()

  const isContract = !!contract
  const isAbsolute = isContract
    ? contract.bidBasis === 'ABSOLUTE'
    : row.offerAmountUnits === 'PRICE'

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    currency,
    format: isAbsolute ? 'currency' : 'percent',
    precision: bidPrecision,
  })

  const placeholderWithStartPrice = intl.formatMessage(
    {
      id: `${ns}.placeholders.offerWithStartPrice`,
    },
    {
      value: formatPrecisionNumber(row.item.customAttributes?.offerStartPrice),
    }
  )

  const hasStartPrice = !!row.item.customAttributes?.offerStartPrice

  const placeholderOverride = hasStartPrice
    ? placeholderWithStartPrice
    : isAbsolute
      ? intl.formatMessage({
          id: `${ns}.placeholders.contract_ABSOLUTE`,
        })
      : '%'

  return isContract ? (
    <FlexContainer>
      <InputWrap>
        <CellInputNumber
          columnId="offerAmount"
          currency={currency}
          disabled={row.disabled}
          format={isAbsolute ? 'currency' : 'percent'}
          ns={ns}
          precision={bidPrecision}
          rowIndex={rowIndex}
          rowsId={rowsId}
          style={{ width: hasStartPrice ? '11.5rem' : '7.5rem' }}
          placeholderOverride={placeholderOverride}
        />
      </InputWrap>
      {isAbsolute ? (
        <>
          {' /'}
          <ContractBidFulfillmentUnit contract={contract} />
        </>
      ) : (
        <FormattedMessage id={`${ns}.relativeUnits.${contract.bidUnit}`} />
      )}
    </FlexContainer>
  ) : (
    <>
      <InputWrap>
        <CellInputNumber
          columnId="offerAmount"
          currency={currency}
          disabled={row.disabled}
          format={isAbsolute ? 'currency' : 'percent'}
          ns={ns}
          precision={bidPrecision}
          rowIndex={rowIndex}
          rowsId={rowsId}
          style={{ width: hasStartPrice ? '11.5rem' : '7.5rem' }}
          placeholderOverride={
            row.item.customAttributes?.offerStartPrice
              ? placeholderWithStartPrice
              : intl.formatMessage({
                  id: `${ns}.placeholders.offerAmount_${row.offerAmountUnits}`,
                })
          }
        />
      </InputWrap>
      {' \u00A0'}
      <For>
        <FormattedMessage id={`${ns}.for`} />
      </For>
      {' \u00A0'}
      <InputWrap>
        <CellInputNumber
          columnId="offerQuantity"
          disabled={row.disabled}
          ns={ns}
          precision={1}
          rowIndex={rowIndex}
          rowsId={rowsId}
          style={{ width: '5.5rem' }}
        />
      </InputWrap>
    </>
  )
}
